import React from 'react';
import { FormattedMessage } from 'react-intl';
import {
  LOAD_USER_CHECKIN_PROFILE,
  INSERT_USER_CHECKIN_PROFILE,
  USER_CHECKIN_PROFILE_ERROR,
  PROCESS_NEW_USER_CHECKIN_PROFILE,
  insertUserCheckinProfile,
  PROCESS_USER_CHECKIN_PROFILE,
  USER_CHECKIN_PROFILE_CREATE_ERROR,
  EDIT_USER_CHECKIN_PROFILE,
  CREATE_NEW_USER_CHECKIN_PROFILE,
} from '../actions/userCheckinProfileActions';
import { apiRequest } from '../actions/apiActions';
import { showLoading, hideLoading, addUiMsg } from '../actions/uiActions';
import { apiUrls, apiVersion } from '../../apiUrls';
import {
  wasUpdatedText,
  wasNotCreatedText,
  wasCreatedText,
  wasNotLoadedText,
} from '../../components/shared/SharedInternationalizations';
import { messageTypes } from '../../components/ui/AlertMessage';
import { errorDisplay, redirect } from '../../utilities/general';

export const checkinProfileText = (
  <FormattedMessage
    id="userMiddleware.checkinProfileText"
    defaultMessage="User Check-In Preferences Profile For"
    description="The staff preferences for a user"
  />
);

/**
 * Middleware to trigger an api call to retrieve all of the users
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to retrieve the users
 */
export const loadUserCheckinProfile = (store) => (next) => (action) => {
  next(action);
  if (action.type === LOAD_USER_CHECKIN_PROFILE) {
    const body = null;
    const meta = {
      method: 'GET',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].usercheckinprofile +
        action.payload,
      onSuccess: INSERT_USER_CHECKIN_PROFILE,
      onError: '',
      auth: store.getState().auth.token,
      body: false,
    };
    // store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Middleware to process the updated user preferences
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#insertUser - Triggers replacing the current user information with the updated user information
 * @fires history.push - Tells react-router to switch back to the normal view of the user from the edit view
 * @fires dispatch#userMsgEditSuccess - Triggers showing the user edit success message
 * @fires dispatch#clearUserMsg - Triggers hiding of the user message
 */
export const processNewUserCheckinProfile = (store) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_NEW_USER_CHECKIN_PROFILE) {
    store.dispatch(hideLoading());
    store.dispatch(insertUserCheckinProfile(action.payload));

    const userObj = store.getState().users.users[action.payload.user];

    let url = '/users/' + action.payload.user + '/view/';
    redirect(action, url);

    const messageText = (
      <React.Fragment>
        {checkinProfileText}:
        <span className="text-italic margin-left-5 margin-right-5">
          "{userObj.first_name} {userObj.last_name}"
        </span>
        {wasCreatedText}
      </React.Fragment>
    );
    const message = {
      message: messageText,
      messageType: messageTypes.success,
    };

    store.dispatch(addUiMsg(message));
  }
};

/**
 * Middleware to process the updated user preferences
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#insertUser - Triggers replacing the current user information with the updated user information
 * @fires history.push - Tells react-router to switch back to the normal view of the user from the edit view
 * @fires dispatch#userMsgEditSuccess - Triggers showing the user edit success message
 * @fires dispatch#clearUserMsg - Triggers hiding of the user message
 */
export const processUserCheckinProfile = (store) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_USER_CHECKIN_PROFILE) {
    store.dispatch(hideLoading());
    store.dispatch(insertUserCheckinProfile(action.payload));

    const userObj = store.getState().users.users[action.payload.user];

    let url = '/users/' + action.payload.user + '/view/';
    redirect(action, url);

    const messageText = (
      <React.Fragment>
        {checkinProfileText}:
        <span className="text-italic margin-left-5 margin-right-5">
          "{userObj.first_name} {userObj.last_name}"
        </span>
        {wasUpdatedText}
      </React.Fragment>
    );
    const message = {
      message: messageText,
      messageType: messageTypes.success,
    };

    store.dispatch(addUiMsg(message));
  }
};

/**
 * Middleware to handle when there is an error with the api
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#userMsgArchiveError - Triggers showing the user archive error message
 * @fires dispatch#clearUserMsg - Triggers hiding of the user message
 */
export const userCheckinProfileCreateError = (store) => (next) => (action) => {
  next(action);
  if (action.type === USER_CHECKIN_PROFILE_CREATE_ERROR) {
    store.dispatch(hideLoading());
    const userObj = store.getState().users.users[action.originalPayload.user];
    const messageText = (
      <React.Fragment>
        {checkinProfileText}
        <span className="text-italic margin-left-5 margin-right-5">
          "{userObj.first_name} {userObj.last_name}"
        </span>
        {wasNotCreatedText}
      </React.Fragment>
    );
    const errors = action.payload;
    const message = {
      message: messageText,
      error: errorDisplay(errors),
      messageType: messageTypes.danger,
    };
    store.dispatch(addUiMsg(message));
    console.error(action);
  }
};

/**
 * Middleware to trigger an api call to retrieve all of the users
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to retrieve the users
 */
export const addUserCheckinProfile = (store) => (next) => (action) => {
  next(action);
  if (action.type === CREATE_NEW_USER_CHECKIN_PROFILE) {
    let excStart = new Date();
    if (action.payload.exclusion_start_time) {
      if (new Date(action.payload.exclusion_start_time) > new Date()) {
        excStart = new Date(action.payload.exclusion_start_time);
      }
    }
    let excEnd = excStart;
    if (action.payload.exclusion_end_time) {
      if (new Date(action.payload.exclusion_end_time) > excStart) {
        excEnd = new Date(action.payload.exclusion_end_time);
      }
    }
    let exStart = null;
    let exEnd = null;
    //refer comments in line 249
    if (action.payload.checkinExclude) {
      exStart = `${excStart.getUTCFullYear()}-${
        excStart.getUTCMonth() + 1
      }-${excStart.getUTCDate()}`;
      exEnd = `${excEnd.getUTCFullYear()}-${
        excEnd.getUTCMonth() + 1
      }-${excEnd.getUTCDate()}`;
    }

    const body = {
      ...action.payload,
      checkin_device: action.payload.checkin_device
        ? parseInt(action.payload.checkin_device)
        : null,
      exclusion_start_time: exStart,
      exclusion_end_time: exEnd,
    };
    const meta = {
      method: 'POST',
      url:
        apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].usercheckinprofile,
      onSuccess: PROCESS_USER_CHECKIN_PROFILE,
      onError: USER_CHECKIN_PROFILE_CREATE_ERROR,
      auth: store.getState().auth.token,
      body: true,
      navigate: action.navigate,
      formSteps: action.formSteps,
      previous: action.previous,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};
/**
 * Middleware to trigger an api call to retrieve all of the users
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to retrieve the users
 */
export const editUserCheckinProfile = (store) => (next) => (action) => {
  next(action);
  if (action.type === EDIT_USER_CHECKIN_PROFILE) {
    let excStart = new Date();
    if (action.payload.exclusion_start_time) {
      if (new Date(action.payload.exclusion_start_time) > new Date()) {
        excStart = new Date(action.payload.exclusion_start_time);
      }
    }
    let excEnd = excStart;
    if (action.payload.exclusion_end_time) {
      if (new Date(action.payload.exclusion_end_time) > excStart) {
        excEnd = new Date(action.payload.exclusion_end_time);
      }
    }
    let exStart = null;
    let exEnd = null;
    // WARNING!!
    // using getUTCDate() can cause the selected date to be less than one day for timezones ahead of UTC
    // e.g: For date 06/10/2022 IST(+5:30) --> getUTCDate() return 09 instead of 10
    // because the offset of timezone(+5:30) is subtracted while converting to UTC
    // But this issue dont happen for timezones that are behind UTC
    // possible ways to handle: 
    // 1.dont need to convert to UTC (because we only care about dates)
    // 2.add the offset time to time of the date and then convert to UTC
    if (action.payload.checkinExclude) {
      exStart = `${excStart.getUTCFullYear()}-${
        excStart.getUTCMonth() + 1
      }-${excStart.getUTCDate()}`;
      exEnd = `${excEnd.getUTCFullYear()}-${
        excEnd.getUTCMonth() + 1
      }-${excEnd.getUTCDate()}`;
    }

    const body = {
      ...action.payload,
      checkin_device: action.payload.checkin_device
        ? parseInt(action.payload.checkin_device)
        : null,
      exclusion_start_time: exStart,
      exclusion_end_time: exEnd,
    };
    const meta = {
      method: 'PATCH',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].usercheckinprofile +
        action.payload.user +
        '/',
      onSuccess: PROCESS_USER_CHECKIN_PROFILE,
      onError: USER_CHECKIN_PROFILE_ERROR,
      auth: store.getState().auth.token,
      body: true,
      navigate: action.navigate,
      formSteps: action.formSteps,
      previous: action.previous,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Middleware to handle when there is an error with the api
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#userMsgArchiveError - Triggers showing the user archive error message
 * @fires dispatch#clearUserMsg - Triggers hiding of the user message
 */
export const userCheckinProfileError = (store) => (next) => (action) => {
  next(action);
  if (action.type === USER_CHECKIN_PROFILE_ERROR) {
    store.dispatch(hideLoading());
    const userObj = store.getState().users.users[action.originalPayload.user];
    const messageText = (
      <React.Fragment>
        {checkinProfileText}
        <span className="text-italic margin-left-5 margin-right-5">
          "{userObj.first_name} {userObj.last_name}"
        </span>
        {wasNotLoadedText}
      </React.Fragment>
    );
    const errors = action.payload;
    const message = {
      message: messageText,
      error: errorDisplay(errors),
      messageType: messageTypes.danger,
    };
    store.dispatch(addUiMsg(message));
    console.error(action);
  }
};

export const userCheckinProfileMdl = [
  loadUserCheckinProfile,
  processNewUserCheckinProfile,
  userCheckinProfileError,
  userCheckinProfileCreateError,
  processUserCheckinProfile,
  editUserCheckinProfile,
  addUserCheckinProfile,
];
